import type { ConstantsUserResponse } from "@gosaas/types";
import { createContext, useContext, useEffect, useState } from "react";
import { apiClient, requestWithHandling } from "@/utils/api";
import { getAccessToken } from "@/utils/local-storage";

export type User = {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	emoji: string;
	isVerified: boolean;
	mfaEnabled: boolean;
	mfaRecoveryCodesCount: number;
	/** Staff console role ("Staff" | "StaffReadOnly"); empty for regular users. */
	staffRole: string;
	createdAt: string;
	updatedAt: string;
};

type AuthContextType = {
	isAuthenticated: boolean;
	isInitialLoading: boolean;
	user: User | null;
	refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

const mapApiResponseToUser = (userData: ConstantsUserResponse): User => ({
	id: userData.id,
	email: userData.email,
	firstName: userData.first_name,
	lastName: userData.last_name,
	emoji: userData.emoji ? decodeURIComponent(userData.emoji) : "",
	isVerified: userData.is_verified,
	mfaEnabled: userData.mfa_enabled,
	mfaRecoveryCodesCount: userData.mfa_recovery_codes_count,
	staffRole: userData.staff_role ?? "",
	createdAt: userData.created_at,
	updatedAt: userData.updated_at,
});

function getInitialAuthState(): {
	isInitialLoading: boolean;
	user: User | null;
} {
	// Prerender/SSR never runs the mount fetch; first client paint must match that HTML.
	return { isInitialLoading: false, user: null };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const [state, setState] = useState(getInitialAuthState);

	const fetchUser = async () => {
		const token = getAccessToken();

		if (!token) {
			setState({ isInitialLoading: false, user: null });
			return;
		}

		try {
			const response = await requestWithHandling<ConstantsUserResponse>(() =>
				apiClient.api.userList(),
			);

			setState({
				isInitialLoading: false,
				user: response.data ? mapApiResponseToUser(response.data) : null,
			});
		} catch (_error) {
			setState({ isInitialLoading: false, user: null });
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: We want to fetch the user on mount
	useEffect(() => {
		if (getAccessToken()) {
			setState({ isInitialLoading: true, user: null });
		}
		fetchUser();
	}, []);

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated: !!state.user,
				isInitialLoading: state.isInitialLoading,
				user: state.user,
				refresh: fetchUser,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

/** Guest defaults when AuthProvider is absent (e.g. React Fast Refresh during dev). */
const authGuestState: AuthContextType = {
	isAuthenticated: false,
	isInitialLoading: false,
	user: null,
	refresh: async () => {},
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

/** Like useAuth but never throws — safe for site chrome that must survive HMR remounts. */
export const useAuthOptional = (): AuthContextType => {
	return useContext(AuthContext) ?? authGuestState;
};
